import { Button } from '@/design-system';
import type { PublicLanguage, PublicReportProblem, PublicTenant } from '@/entities/publicReport';

import type { PublicCopy, PublicProblemKey } from '../copy';
import { formatDate } from '../format';
import { TenantMark } from './TenantMark';

export interface PublicProblemProps {
    problem: PublicReportProblem;
    tenant: PublicTenant | null;
    copy: PublicCopy;
    language: PublicLanguage;
    /** `1` op de publieke pagina, `2` in de admin-preview. */
    headingLevel: 1 | 2;
    onRetry?: () => void;
}

/** Deze redenen kunnen met opnieuw proberen verdwijnen; de rest niet. */
const RETRYABLE: ReadonlySet<PublicProblemKey> = new Set(['offline', 'unknown', 'tooManyAttempts']);

/**
 * Een verlopen, ingetrokken of onbekende link krijgt een rustige pagina met een
 * eigen uitleg - geen stacktrace, geen kale 404. Een verlopen link noemt de
 * datum tot wanneer hij werkte. Offline is hier geen fout maar een mededeling.
 */
export function PublicProblem({
    problem,
    tenant,
    copy,
    language,
    headingLevel,
    onRetry,
}: PublicProblemProps) {
    const reason = problemKeyOf(problem);
    const Heading = headingLevel === 1 ? 'h1' : 'h2';
    const help = tenant?.phone ? copy.problemHelp(tenant.phone) : copy.problemHelpNoPhone;
    const expiredOn = formatDate(problem.expiredAt, language);

    const body =
        reason === 'expired' && expiredOn
            ? copy.expiredOn(expiredOn)
            : reason === 'tooManyAttempts' && problem.retryAfterMinutes !== null
              ? copy.tooManyAttemptsWait(problem.retryAfterMinutes)
              : copy.problemBodies[reason];

    return (
        <div
            role={reason === 'offline' ? 'status' : 'alert'}
            className="flex flex-col gap-3 px-[var(--pr-pad-x)] py-10"
        >
            {tenant ? <TenantMark tenant={tenant} /> : null}

            <Heading className="m-0 font-[family-name:var(--font-display)] text-[clamp(22px,4cqi,26px)] leading-[1.15] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                {copy.problemTitles[reason]}
            </Heading>

            <p className="m-0 text-[14px] leading-[1.55] text-[var(--text-secondary)]">{body}</p>

            <p className="m-0 text-[14px] leading-[1.55] text-[var(--text-tertiary-safe)]">
                {help}
            </p>

            {onRetry && RETRYABLE.has(reason) ? (
                <div className="mt-2">
                    <Button variant="outline" size="md" onClick={onRetry}>
                        {copy.retry}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

function problemKeyOf(problem: PublicReportProblem): PublicProblemKey {
    switch (problem.kind) {
        case 'notFound':
        case 'expired':
        case 'revoked':
        case 'tooManyAttempts':
        case 'offline':
            return problem.kind;
        default:
            return 'unknown';
    }
}
